import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../../../context/API';
import { Promotion, PromotionStats, Merchant, PromotionFormData } from '../types';

// Mapping functions
const mapTypeToBackend = (frontendType: string): string => {
  const typeMap: Record<string, string> = {
    percentage: "percentage_discount",
    fixed: "fixed_amount_discount",
    buy_one_get_one: "buy_x_get_y",
  };
  return typeMap[frontendType] || frontendType;
};

const mapTypeToFrontend = (backendType: string): string => {
  const typeMap: Record<string, string> = {
    percentage_discount: "percentage",
    fixed_amount_discount: "fixed",
    buy_x_get_y: "buy_one_get_one",
    free_delivery: "free_delivery",
    minimum_order_discount: "minimum_order_discount",
  };
  return typeMap[backendType] || backendType;
};

export const mapFormDataToBackend = (formData: PromotionFormData): Record<string, any> => {
  const startDate = formData.startDate
    ? new Date(formData.startDate + "T00:00:00Z").toISOString()
    : null;
  const endDate = formData.endDate
    ? new Date(formData.endDate + "T23:59:59Z").toISOString()
    : null;

  return {
    name: formData.title,
    description: formData.description || "",
    promoCode: formData.code || "",
    type: mapTypeToBackend(formData.type),
    discountValue: formData.discountValue || 0,
    minimumOrderAmount: formData.minOrderAmount || 0,
    maximumDiscountAmount: formData.maxDiscount || 0,
    usageLimit: formData.usageLimit || 0,
    startDate: startDate,
    endDate: endDate,
    status: formData.isActive ? "active" : "draft",
  };
};

export const mapBackendToFormData = (backendData: any): PromotionFormData => {
  const startDate = backendData.startDate
    ? new Date(backendData.startDate).toISOString().split("T")[0]
    : "";
  const endDate = backendData.endDate
    ? new Date(backendData.endDate).toISOString().split("T")[0]
    : "";

  return {
    title: backendData.name || "",
    description: backendData.description || "",
    code: backendData.promoCode || backendData.code || "",
    type: mapTypeToFrontend(backendData.type) as "percentage" | "fixed" | "buy_one_get_one",
    discountValue: backendData.discountValue || 0,
    minOrderAmount: backendData.minimumOrderAmount || 0,
    maxDiscount: backendData.maximumDiscountAmount || 0,
    startDate: startDate,
    endDate: endDate,
    isActive: backendData.status === "active" || backendData.isActive === true,
    usageLimit: backendData.usageLimit || 0,
    usageCount: backendData.usageCount || 0,
  };
};

// Promotions API endpoints
const PROMOTIONS_ENDPOINTS = {
  LIST: "/promotions",
  GET: "/promotions/{id}",
  CREATE: "/promotions",
  UPDATE: "/promotions/{id}",
  DELETE: "/promotions/{id}",
  ACTIVATE: "/promotions/{id}/activate",
  PAUSE: "/promotions/{id}/pause",
  CANCEL: "/promotions/{id}/cancel",
  STATS: "/promotions/stats",
};

// Helper function to get endpoint with ID replacement
const getEndpoint = (template: string, id: string | number): string => 
  template.replace("{id}", String(id));

interface PromotionsState {
  promotions: Promotion[];
  selectedPromotion: Promotion | null;
  merchants: Merchant[];
  selectedMerchantId: number | null;
  stats: PromotionStats;
  loading: boolean;
  loadingStats: boolean;
  loadingMerchants: boolean;
  loadingDetails: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: PromotionsState = {
  promotions: [],
  selectedPromotion: null,
  merchants: [],
  selectedMerchantId: null,
  stats: {
    total: 0,
    active: 0,
    draft: 0,
    paused: 0,
    expired: 0,
    cancelled: 0,
  },
  loading: false,
  loadingStats: false,
  loadingMerchants: false,
  loadingDetails: false,
  saving: false,
  deleting: false,
  error: null,
};

// Async thunks
export const fetchMerchants = createAsyncThunk<
  Merchant[],
  void,
  { rejectValue: string }
>(
  'promotions/fetchMerchants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get("merchant", {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      const merchantsData = response.data.data || [];
      return merchantsData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch merchants"
      );
    }
  }
);

export const fetchPromotions = createAsyncThunk<
  Promotion[],
  { merchantId: string | number; filters?: Record<string, any> },
  { rejectValue: string }
>(
  'promotions/fetchPromotions',
  async ({ merchantId, filters = {} }, { rejectWithValue }) => {
    try {
      const backendFilters: Record<string, any> = {};
      if (filters.status && filters.status !== "all") {
        backendFilters.status = filters.status;
      }
      if (filters.type && filters.type !== "all") {
        backendFilters.type = mapTypeToBackend(filters.type);
      }
      
      const params: Record<string, any> = { merchantId: String(merchantId), ...backendFilters };
      const response = await API.get(PROMOTIONS_ENDPOINTS.LIST, { params });
      const promotionsData = response.data || [];
      const mappedPromotions = Array.isArray(promotionsData)
        ? promotionsData.map((promo: any) => {
            const mapped = mapBackendToFormData(promo);
            return {
              ...mapped,
              id: promo.id,
              status: promo.status,
              isExpired: promo.endDate ? new Date(promo.endDate) < new Date() : false,
            };
          })
        : [];
      return mappedPromotions;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch promotions"
      );
    }
  }
);

export const fetchPromotion = createAsyncThunk<
  Promotion,
  { promotionId: string | number; merchantId: string | number },
  { rejectValue: string }
>(
  'promotions/fetchPromotion',
  async ({ promotionId, merchantId }, { rejectWithValue }) => {
    try {
      const endpoint = getEndpoint(PROMOTIONS_ENDPOINTS.GET, promotionId);
      const params = { merchantId: String(merchantId) };
      const response = await API.get(endpoint, { params });
      const fullPromotion = response.data;
      const mappedData = mapBackendToFormData(fullPromotion);
      return { ...fullPromotion, ...mappedData };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch promotion"
      );
    }
  }
);

export const createPromotion = createAsyncThunk<
  Promotion,
  { formData: PromotionFormData; merchantId: string | number },
  { rejectValue: string }
>(
  'promotions/createPromotion',
  async ({ formData, merchantId }, { rejectWithValue }) => {
    try {
      const backendData = mapFormDataToBackend(formData);
      const params = { merchantId: String(merchantId) };
      const response = await API.post(PROMOTIONS_ENDPOINTS.CREATE, backendData, { params });
      const mappedData = mapBackendToFormData(response.data);
      return { ...response.data, ...mappedData };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to create promotion"
      );
    }
  }
);

export const updatePromotion = createAsyncThunk<
  Promotion,
  { promotionId: string | number; formData: PromotionFormData; merchantId: string | number },
  { rejectValue: string }
>(
  'promotions/updatePromotion',
  async ({ promotionId, formData, merchantId }, { rejectWithValue }) => {
    try {
      const backendData = mapFormDataToBackend(formData);
      const endpoint = getEndpoint(PROMOTIONS_ENDPOINTS.UPDATE, promotionId);
      const params = { merchantId: String(merchantId) };
      const response = await API.put(endpoint, backendData, { params });
      const mappedData = mapBackendToFormData(response.data);
      return { ...response.data, ...mappedData };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update promotion"
      );
    }
  }
);

export const deletePromotion = createAsyncThunk<
  void,
  { promotionId: string | number; merchantId: string | number },
  { rejectValue: string }
>(
  'promotions/deletePromotion',
  async ({ promotionId, merchantId }, { rejectWithValue }) => {
    try {
      const endpoint = getEndpoint(PROMOTIONS_ENDPOINTS.DELETE, promotionId);
      const params = { merchantId: String(merchantId) };
      await API.delete(endpoint, { params });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete promotion"
      );
    }
  }
);

export const fetchPromotionStats = createAsyncThunk<
  PromotionStats,
  string | number,
  { rejectValue: string }
>(
  'promotions/fetchPromotionStats',
  async (merchantId, { rejectWithValue }) => {
    try {
      const params = { merchantId: String(merchantId) };
      const response = await API.get(PROMOTIONS_ENDPOINTS.STATS, { params });
      const statsData = response.data || {};
      return {
        total: statsData.total || 0,
        active: statsData.active || 0,
        draft: statsData.draft || 0,
        paused: statsData.paused || 0,
        expired: statsData.expired || 0,
        cancelled: statsData.cancelled || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch promotion stats"
      );
    }
  }
);

const promotionsSlice = createSlice({
  name: 'promotions',
  initialState,
  reducers: {
    setSelectedPromotion: (state, action: PayloadAction<Promotion | null>) => {
      state.selectedPromotion = action.payload;
    },
    clearSelectedPromotion: (state) => {
      state.selectedPromotion = null;
    },
    setSelectedMerchantId: (state, action: PayloadAction<number | null>) => {
      state.selectedMerchantId = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch merchants
    builder
      .addCase(fetchMerchants.pending, (state) => {
        state.loadingMerchants = true;
        state.error = null;
      })
      .addCase(fetchMerchants.fulfilled, (state, action) => {
        state.loadingMerchants = false;
        state.merchants = action.payload;
        if (action.payload.length > 0 && !state.selectedMerchantId) {
          state.selectedMerchantId = action.payload[0].id;
        }
      })
      .addCase(fetchMerchants.rejected, (state, action) => {
        state.loadingMerchants = false;
        state.error = action.payload as string;
        state.merchants = [];
      });

    // Fetch promotions
    builder
      .addCase(fetchPromotions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        state.loading = false;
        state.promotions = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPromotions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.promotions = [];
      });

    // Fetch single promotion
    builder
      .addCase(fetchPromotion.pending, (state) => {
        state.loadingDetails = true;
        state.error = null;
      })
      .addCase(fetchPromotion.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.selectedPromotion = action.payload;
        // Update in list if exists
        const index = state.promotions.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.promotions[index] = action.payload;
        }
      })
      .addCase(fetchPromotion.rejected, (state, action) => {
        state.loadingDetails = false;
        state.error = action.payload as string;
      });

    // Create promotion
    builder
      .addCase(createPromotion.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createPromotion.fulfilled, (state, action) => {
        state.saving = false;
        state.promotions.unshift(action.payload);
      })
      .addCase(createPromotion.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Update promotion
    builder
      .addCase(updatePromotion.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updatePromotion.fulfilled, (state, action) => {
        state.saving = false;
        state.selectedPromotion = action.payload;
        const index = state.promotions.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.promotions[index] = action.payload;
        }
      })
      .addCase(updatePromotion.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Delete promotion
    builder
      .addCase(deletePromotion.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deletePromotion.fulfilled, (state, action) => {
        state.deleting = false;
        const promotionId = action.meta.arg.promotionId;
        state.promotions = state.promotions.filter(p => p.id !== promotionId);
        if (state.selectedPromotion?.id === promotionId) {
          state.selectedPromotion = null;
        }
      })
      .addCase(deletePromotion.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });

    // Fetch stats
    builder
      .addCase(fetchPromotionStats.pending, (state) => {
        state.loadingStats = true;
        state.error = null;
      })
      .addCase(fetchPromotionStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchPromotionStats.rejected, (state, action) => {
        state.loadingStats = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedPromotion,
  clearSelectedPromotion,
  setSelectedMerchantId,
  setError,
  clearError,
} = promotionsSlice.actions;

export default promotionsSlice.reducer;

